import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useTbdCheck from "../../../functions/useTbdCheck";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectGeneralStation,
  selectHcRequirements,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import useFieldClass from "../../../functions/useFieldClass";
import { getHCConfigurationFlags } from './hcRequirementsVisibility';

function HCRequirements({ zone }) {
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const hcsectionName = "hcRequirements";
  const generalSpecSectionName = "generalStation";

  const apiUrl = apiUrls[hcsectionName];
  const generalSpecApiUrl = apiUrls[generalSpecSectionName];

  const hcTableKey = "hcRequirements";
  const generalSpecTableKey = "generalStation";

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const error = null;

  const storedHcJobDetails = useSelector(selectHcRequirements);
  const storedGeneralSpecJobDetails = useSelector(selectGeneralStation);

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[hcsectionName] !== "success"; // Determine loading state based on API status

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );
  const generalJobDetails = useMemo(
    () =>
      storedGeneralSpecJobDetails.find((detail) => detail.Zone === activetab) ||
      {},
    [storedGeneralSpecJobDetails, activetab]
  );
  const jobDetails = useMemo(
    () => storedHcJobDetails.find((detail) => detail.Zone === activetab) || {},
    [storedHcJobDetails, activetab]
  );

  const { formData, handleChange, setFormData } = useFormState({}); // Form state management

  useEffect(() => {
    if (generalJobDetails) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        generalSpecsID: generalJobDetails.ID || "",
        dynamicInletPSI: generalJobDetails.DynamicInletPSI || "",
        feetOfLift: generalJobDetails.FeetOfLift || "",
        primeSystem: generalJobDetails.PrimeSystem || "",
        stationConfiguration: generalJobDetails.StationConfiguration || "",
      }));
    }
  }, [generalJobDetails, setFormData]);

  useEffect(() => {
    if (jobDetails) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        ID: jobDetails.ID || "",
        backflowPreventer: jobDetails.BackflowPreventer || "",
        comments: jobDetails.Comments || "",
        customProviderDetails: jobDetails.CustomProviderDetails || "",
        footValveDetails: jobDetails.FootValveDetails || "",
        footValveRequired: jobDetails.FootValveRequired || "",
        inletPressureControl: jobDetails.InletPressureControl || "",
        stationDrawingFrom: jobDetails.StationDrawingFrom || "",
        suctionLineConfig: jobDetails.SuctionLineConfig || "",
        suctionLineMaterial: jobDetails.SuctionLineMaterial || "",
        suctionLinesBy: jobDetails.SuctionLinesBy || "",
        vacuumPrimeDetail: jobDetails.VacuumPrimeDetail || "",
        zone: jobDetails.Zone || "",
      }));
    }
  }, [jobDetails, setFormData]);

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  /****************Add dropdown options here*************************/

  const suctionLineMaterialOptions =
    options
      ?.filter((option) => option.DropdownType === "Suction Line Material")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationDrawingFromOptions =
    options
      ?.filter((option) => option.DropdownType === "Station Drawing From")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const primeSystemOptions =
    options
      ?.filter((option) => option.DropdownType === "Prime System")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const suctionLinesByOptions =
    options
      ?.filter((option) => option.DropdownType === "Suction Lines By")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const suctionLineConfigOptions =
    options
      ?.filter((option) => option.DropdownType === "Suction Line Config")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const backflowPreventerOptions =
    options
      ?.filter((option) => option.DropdownType === "Backflow Preventer")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const inletPressureControlOptions =
    options
      ?.filter((option) => option.DropdownType === "Inlet Pressure Control")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const footValveRequiredOptions =
    options
      ?.filter((option) => option.DropdownType === "Foot Valve Required")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /******************************************************************/

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  useTbdCheck(formData, zone); // Pass zone to track TBD status for specific zone

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  // Determine which fields to show based on station configuration
  const { isBoosterOrFloodedSuction, isSuctionLift } = getHCConfigurationFlags(formData.stationConfiguration);

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div>
            <Form
              onSubmit={handleSubmit}
              className="mt-4 mechanical-responsive-grid"
            >
              {/* Fields shown for all types except when limiting to specific fields for Booster/Flooded Suction */}
              {!isBoosterOrFloodedSuction && (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="feetOfLift">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("FeetOfLift")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Feet Of Lift</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            name="feetOfLift"
                            value={formData.feetOfLift || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Feet Of Lift", [], {
                                ID: formData.generalSpecsID,
                                url: generalSpecApiUrl,
                                field: "FeetOfLift",
                                tableKey: generalSpecTableKey,
                              })
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.feetOfLift}
                            onChange={handleChange}
                            placeholder="Enter Feet Of Lift"
                          />
                        </Col>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group as={Row} controlId="suctionLineMaterial">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("SuctionLineMaterial")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Suction Line Material</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Select
                            name="suctionLineMaterial"
                            value={formData.suctionLineMaterial || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(
                                e,
                                "Suction Line Material",
                                suctionLineMaterialOptions,
                                {
                                  ID: formData.ID,
                                  url: apiUrl,
                                  field: "SuctionLineMaterial",
                                  tableKey: hcTableKey,
                                }
                              )
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.suctionLineMaterial}
                            onChange={handleChange}
                          >
                            <option value="">Select Suction Line Material</option>
                            {suctionLineMaterialOptions.map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                            {!isValueInDropdown(
                              formData.suctionLineMaterial,
                              suctionLineMaterialOptions
                            ) &&
                              formData.suctionLineMaterial && (
                                <option
                                  value={formData.suctionLineMaterial}
                                  style={{ color: "red" }}
                                >
                                  *{formData.suctionLineMaterial}
                                </option>
                            )}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              {!isBoosterOrFloodedSuction && (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="stationDrawingFrom">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("StationDrawingFrom")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Station Drawing From</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Select
                            name="stationDrawingFrom"
                            value={formData.stationDrawingFrom || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(
                                e,
                                "Station Drawing From",
                                stationDrawingFromOptions,
                                {
                                  ID: formData.ID,
                                  url: apiUrl,
                                  field: "StationDrawingFrom",
                                  tableKey: hcTableKey,
                                }
                              )
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.stationDrawingFrom}
                            onChange={handleChange}
                          >
                            <option value="">Select Station Drawing From</option>
                            {stationDrawingFromOptions.map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                            {!isValueInDropdown(
                              formData.stationDrawingFrom,
                              stationDrawingFromOptions
                            ) &&
                              formData.stationDrawingFrom && (
                                <option
                                  value={formData.stationDrawingFrom}
                                  style={{ color: "red" }}
                                >
                                  *{formData.stationDrawingFrom}
                                </option>
                            )}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group as={Row} controlId="primeSystem">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("PrimeSystem")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Prime System</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Select
                            name="primeSystem"
                            value={formData.primeSystem || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Prime System", primeSystemOptions, {
                                ID: formData.generalSpecsID,
                                url: generalSpecApiUrl,
                                field: "PrimeSystem",
                                tableKey: generalSpecTableKey,
                              })
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.primeSystem}
                            onChange={handleChange}
                          >
                            <option value="">Select Prime System</option>
                            {primeSystemOptions.map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                            {!isValueInDropdown(
                              formData.primeSystem,
                              primeSystemOptions
                            ) &&
                              formData.primeSystem && (
                                <option
                                  value={formData.primeSystem}
                                  style={{ color: "red" }}
                                >
                                  *{formData.primeSystem}
                                </option>
                            )}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              {!isBoosterOrFloodedSuction && (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="suctionLinesBy">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("SuctionLinesBy")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Suction Lines By</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Select
                            name="suctionLinesBy"
                            value={formData.suctionLinesBy || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(
                                e,
                                "Suction Lines By",
                                suctionLinesByOptions,
                                {
                                  ID: formData.ID,
                                  url: apiUrl,
                                  field: "SuctionLinesBy",
                                  tableKey: hcTableKey,
                                }
                              )
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.suctionLinesBy}
                            onChange={handleChange}
                          >
                            <option value="">Select Suction Lines By</option>
                            {suctionLinesByOptions.map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                            {!isValueInDropdown(
                              formData.suctionLinesBy,
                              suctionLinesByOptions
                            ) &&
                              formData.suctionLinesBy && (
                                <option
                                  value={formData.suctionLinesBy}
                                  style={{ color: "red" }}
                                >
                                  *{formData.suctionLinesBy}
                                </option>
                            )}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group as={Row} controlId="vacuumPrimeDetail">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("VacuumPrimeDetail")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Vacuum Prime Detail</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            name="vacuumPrimeDetail"
                            value={formData.vacuumPrimeDetail || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Vacuum Prime Detail", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "VacuumPrimeDetail",
                                tableKey: hcTableKey,
                              })
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.vacuumPrimeDetail}
                            onChange={handleChange}
                            placeholder="Enter Vacuum Prime Detail"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              {!isBoosterOrFloodedSuction && (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="customProviderDetails">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("CustomProviderDetails")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Custom Provider Details</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            name="customProviderDetails"
                            value={formData.customProviderDetails || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Custom Provider Details", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "CustomProviderDetails",
                                tableKey: hcTableKey,
                              })
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.customProviderDetails}
                            onChange={handleChange}
                            placeholder="Enter Custom Provider Details"
                          />
                        </Col>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group as={Row} controlId="footValveRequired">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("FootValveRequired")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Foot Valve Required</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Select
                            name="footValveRequired"
                            value={formData.footValveRequired || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(
                                e,
                                "Foot Valve Required",
                                footValveRequiredOptions,
                                {
                                  ID: formData.ID,
                                  url: apiUrl,
                                  field: "FootValveRequired",
                                  tableKey: hcTableKey,
                                }
                              )
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.footValveRequired}
                            onChange={handleChange}
                          >
                            <option value="">Select Foot Valve Required</option>
                            {footValveRequiredOptions.map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                            {!isValueInDropdown(
                              formData.footValveRequired,
                              footValveRequiredOptions
                            ) &&
                              formData.footValveRequired && (
                                <option
                                  value={formData.footValveRequired}
                                  style={{ color: "red" }}
                                >
                                  *{formData.footValveRequired}
                                </option>
                            )}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              {!isBoosterOrFloodedSuction && (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group as={Row} controlId="suctionLineConfig">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("SuctionLineConfig")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Suction Line Config</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Select
                            name="suctionLineConfig"
                            value={formData.suctionLineConfig || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(
                                e,
                                "Suction Line Config",
                                suctionLineConfigOptions,
                                {
                                  ID: formData.ID,
                                  url: apiUrl,
                                  field: "SuctionLineConfig",
                                  tableKey: hcTableKey,
                                }
                              )
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.suctionLineConfig}
                            onChange={handleChange}
                          >
                            <option value="">Select Suction Line Config</option>
                            {suctionLineConfigOptions.map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                            {!isValueInDropdown(
                              formData.suctionLineConfig,
                              suctionLineConfigOptions
                            ) &&
                              formData.suctionLineConfig && (
                                <option
                                  value={formData.suctionLineConfig}
                                  style={{ color: "red" }}
                                >
                                  *{formData.suctionLineConfig}
                                </option>
                            )}
                          </Form.Select>
                        </Col>
                      </Form.Group>
                    </Col>

                    <Col>
                      <Form.Group as={Row} controlId="footValveDetails">
                        <Form.Label column md={3}>
                          <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip("FootValveDetails")}
                            delay={{ show: 300, hide: 200 }}
                          >
                            <span>Foot Valve Details</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            name="footValveDetails"
                            value={formData.footValveDetails || ""}
                            onFocus={handleFocus}
                            onBlur={(e) =>
                              handleBlur(e, "Foot Valve Details", [], {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "FootValveDetails",
                                tableKey: hcTableKey,
                              })
                            }
                            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                            className={fieldClasses.footValveDetails}
                            onChange={handleChange}
                            placeholder="Enter Foot Valve Details"
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              {/* Backflow Preventer and Inlet Pressure Control - Show for Booster/Flooded Suction, Hide for Suction Lift */}
              {!isSuctionLift && (
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="backflowPreventer">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("BackflowPreventer")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Backflow Preventer</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="backflowPreventer"
                          value={formData.backflowPreventer || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Backflow Preventer",
                              backflowPreventerOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "BackflowPreventer",
                                tableKey: hcTableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.backflowPreventer}
                          onChange={handleChange}
                        >
                          <option value="">Select Backflow Preventer</option>
                          {backflowPreventerOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.backflowPreventer,
                            backflowPreventerOptions
                          ) &&
                            formData.backflowPreventer && (
                              <option
                                value={formData.backflowPreventer}
                                style={{ color: "red" }}
                              >
                                *{formData.backflowPreventer}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group as={Row} controlId="inletPressureControl">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("InletPressureControl")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Inlet Pressure Control</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Select
                          name="inletPressureControl"
                          value={formData.inletPressureControl || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(
                              e,
                              "Inlet Pressure Control",
                              inletPressureControlOptions,
                              {
                                ID: formData.ID,
                                url: apiUrl,
                                field: "InletPressureControl",
                                tableKey: hcTableKey,
                              }
                            )
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.inletPressureControl}
                          onChange={handleChange}
                        >
                          <option value="">Select Inlet Pressure Control</option>
                          {inletPressureControlOptions.map((option) => (
                            <option key={option.ID} value={option.DisplayText}>
                              {option.DisplayText}
                            </option>
                          ))}
                          {!isValueInDropdown(
                            formData.inletPressureControl,
                            inletPressureControlOptions
                          ) &&
                            formData.inletPressureControl && (
                              <option
                                value={formData.inletPressureControl}
                                style={{ color: "red" }}
                              >
                                *{formData.inletPressureControl}
                              </option>
                          )}
                        </Form.Select>
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              )}
              {/* Dynamic Inlet PSI - Show for Booster/Flooded Suction, Hide for Suction Lift */}
              {!isSuctionLift && (
                <Row className="mb-3">
                  <Col>
                    <Form.Group as={Row} controlId="dynamicInletPSI">
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("DynamicInletPSI")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Dynamic Inlet PSI</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="text"
                          name="dynamicInletPSI"
                          value={formData.dynamicInletPSI || ""}
                          onFocus={handleFocus}
                          onBlur={(e) =>
                            handleBlur(e, "Dynamic Inlet PSI", [], {
                              ID: formData.generalSpecsID,
                              url: generalSpecApiUrl,
                              field: "DynamicInletPSI",
                              tableKey: generalSpecTableKey,
                            })
                          }
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={fieldClasses.dynamicInletPSI}
                          onChange={handleChange}
                          placeholder="Enter Dynamic Inlet PSI"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col />
                </Row>
              )}
            </Form>

            <Form
              onSubmit={handleSubmit}
              className="mt-4 mechanical-comments-form"
            >
              <Row className="mb-3">
                <Col>
                  <Form.Group controlId="comments">
                    <Form.Label>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("Comments")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Comments</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="comments"
                      value={formData.comments || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Comments", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Comments",
                          tableKey: hcTableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.comments}
                      onChange={handleChange}
                      placeholder="Enter Comments"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HCRequirements;
